import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { WarehousingMasterType, isThirdPartyVirtualWarehouse, masterDataMap, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingLabelPrintService } from '../warehousing-label-print.service';
import { WarehousingDispatch, WarehousingItem, WarehousingMasterData } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { WarehousingWorkHistoryComponent } from '../shipment-work/warehousing-work-history.component';
import { WarehousingDesktopOutboundComponent } from '../shipment-work/warehousing-desktop-outbound.component';
import { WarehousingSupplyReserveComponent } from '../shipment-work/warehousing-supply-reserve.component';
import { itemIdentityLabel } from '../warehousing-item-display';
import { FieldResourcesPanelComponent } from '../../field-work/field-resources-panel.component';

@Component({
  selector: 'app-warehousing-dispatch-view',
  standalone: true,
  imports: [CommonModule, RouterModule, BreadcrumbModule, ButtonModule, ProgressBarModule, TableModule, TagModule, ToastModule, PageDataPanelComponent, PageDetailHeroComponent, PageFormSectionComponent, PageMetricStripComponent, PageShellComponent, FieldResourcesPanelComponent, WarehousingWorkHistoryComponent, WarehousingDesktopOutboundComponent, WarehousingSupplyReserveComponent],
  providers: [MessageService],
  templateUrl: './warehousing-dispatch-view.component.html'
})
export class WarehousingDispatchViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  private labelPrintService = inject(WarehousingLabelPrintService);
  private crmService = inject(CrmService);
  private messageService = inject(MessageService);
  isLoading = false;
  isSubmitting = false;
  isPrintingLabels = false;
  isDownloadingNote = false;
  dispatch?: WarehousingDispatch;
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  items: WarehousingItem[] = [];
  masterData = masterDataMap();
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Dispatch Details', 'Dispatches', '/warehousing/dispatch');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  backLink(): string { return this.route.snapshot.queryParamMap.get('fromShipmentWork') === '1'
    ? this.workReturnLink() : '/warehousing/dispatch'; }

  private workReturnLink(): string {
    const params = this.route.snapshot.queryParamMap;
    const type = params.get('workType');
    const id = params.get('workId');
    return type && id ? `/warehousing/shipment-work/${type}/${id}` : '/warehousing/shipment-work';
  }

  ngOnInit(): void { this.loadDispatch(); }

  loadDispatch(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({
      dispatch: this.warehousingService.getDispatch(id),
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      items: this.warehousingService.getItems(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.dispatch = response.dispatch.data;
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.items = response.items.data || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = response.warehouses.data || [];
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.breadcrumbItems = warehousingBreadcrumb(this.dispatch?.dispatchNumber || 'Dispatch Details', 'Dispatches', '/warehousing/dispatch');
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  isManualGoodsOut(): boolean {
    return this.dispatch?.sourceType === 'MANUAL_GOODS_OUT';
  }

  goodsOutWarehouse(): string {
    const id = this.dispatch?.lines?.[0]?.warehouseId;
    return this.masterName('WAREHOUSE', id);
  }

  downloadGoodsOutNote(): void {
    if (!this.isManualGoodsOut() || this.lifecycleStatus() !== 'DISPATCHED' || !this.dispatch?.id) return;
    this.isDownloadingNote = true;
    this.warehousingService.downloadGdnReport(this.dispatch.id).pipe(
      finalize(() => this.isDownloadingNote = false)
    ).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `gdn-${(this.dispatch?.dispatchNumber || String(this.dispatch?.id)).replace(/[\\/]/g, '-')}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
      },
      error: error => this.messageService.add({
        severity: 'error', summary: 'GDN unavailable',
        detail: error?.error?.message || 'Unable to download the Goods Delivery Note.'
      })
    });
  }

  reserveDispatch(): void {
    if (!this.dispatch?.id || this.isSubmitting) return;
    this.isSubmitting = true;
    this.warehousingService.reserveDispatch(this.dispatch.id).pipe(finalize(() => this.isSubmitting = false)).subscribe({
      next: resp => {
        if (!resp.success || !resp.data) return;
        this.messageService.add({
          severity: 'success',
          summary: 'Reserved',
          detail: this.reserveSuccessDetail(resp.data)
        });
        this.loadDispatch();
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Reserve Failed',
        detail: error?.error?.message || 'Unable to reserve dispatch stock.'
      })
    });
  }

  clientName(id?: number): string { return this.clients.find(client => client.id === id)?.companyName || '-'; }
  accountName(id?: number): string { return this.accounts.find(account => account.id === id)?.companyName || '-'; }
  itemName(id?: number): string {
    const item = this.items.find(row => row.id === id);
    return itemIdentityLabel(item);
  }
  masterName(type: WarehousingMasterType, id?: number): string {
    return (this.masterData[type] as WarehousingMasterData[]).find(item => item.id === id)?.name || '-';
  }

  private warehouseById(id?: number): WarehousingMasterData | undefined {
    return (this.masterData.WAREHOUSE || []).find(item => item.id === id);
  }

  usesVirtualWarehouse(): boolean {
    return (this.dispatch?.lines || []).some(line => isThirdPartyVirtualWarehouse(this.warehouseById(line.warehouseId)));
  }

  metrics(): PageMetric[] {
    const lines = this.dispatch?.lines || [];
    const requested = lines.reduce((sum, line) => sum + (line.quantityRequested || 0), 0);
    const reserved = lines.reduce((sum, line) => sum + (line.quantityReserved || 0), 0);
    const exited = lines.reduce((sum, line) => sum + (line.quantityExited || 0), 0);
    return [
      { label: 'Lines', value: lines.length },
      { label: 'Requested', value: requested },
      { label: 'Reserved', value: reserved, tone: 'warn' },
      { label: 'Exited', value: exited, tone: 'success' }
    ];
  }

  displayDate(value?: string | Date): string {
    if (!value) return '-';
    return value instanceof Date ? value.toISOString().slice(0, 10) : String(value);
  }

  lifecycleStatus(): string {
    return this.dispatch?.lifecycleStatus || this.dispatch?.status || '-';
  }

  showLifecycleTag(): boolean {
    if (!this.dispatch?.lifecycleStatus) return false;
    return (this.dispatch.lifecycleStatus || '').toUpperCase() !== (this.dispatch.status || '').toUpperCase();
  }

  sourceSummary(): string {
    if (!this.dispatch) return '-';
    return this.dispatch.sourceNumber || this.dispatch.referenceNumber || 'Manual dispatch';
  }

  transportSummary(): string {
    if (!this.dispatch) return '-';
    if (this.dispatch.shipmentNumber) return this.dispatch.shipmentNumber;
    if (this.dispatch.transportReleaseNumber) return this.dispatch.transportReleaseNumber;
    return 'Not linked';
  }

  canReserve(): boolean {
    return !this.isManualGoodsOut() && !this.usesVirtualWarehouse()
      && this.isGateOpen()
      && ['PLANNED', 'DRAFT'].includes(this.lifecycleStatus().toUpperCase());
  }

  canChooseSupply(): boolean {
    return !this.isManualGoodsOut() && !this.usesVirtualWarehouse()
      && ['PLANNED', 'DRAFT', 'RESERVED'].includes(this.lifecycleStatus().toUpperCase());
  }

  supplyAutoOpen(): boolean { return this.route.snapshot.queryParamMap.get('reserveSupply') === '1'; }

  gateStatusLabel(): string {
    if (!this.dispatch?.gatedByTransferId) {
      return 'Ungated dispatch';
    }
    return this.isGateOpen()
      ? 'Inbound receipt gate open'
      : 'Waiting for inbound receipt';
  }

  advancePlanningLabel(): string {
    if (!this.dispatch?.shipmentId || !this.dispatch.gatedByTransferId) return '';
    return this.isGateOpen() ? 'Shipment planned · inbound receipt complete'
      : 'Shipment planned · awaiting inbound receipt';
  }

  gateStatusSeverity(): 'success' | 'warn' | 'secondary' {
    if (!this.dispatch?.gatedByTransferId) return 'secondary';
    return this.gateStatusLabel().includes('open') ? 'success' : 'warn';
  }

  isGateOpen(): boolean {
    return !this.dispatch?.gatedByTransferId || this.dispatch.crossDockGateOpen === true;
  }

  lineSource(line: WarehousingDispatch['lines'][number]): string {
    return line.sourceLineNumber || line.sourceLineType || '-';
  }

  reservationCodes(line: WarehousingDispatch['lines'][number]): string {
    const codes = (line.reservations || [])
      .map(reservation => (this.isManualGoodsOut() && reservation.stockUnitSku)
        || reservation.stockUnitCode || `Unit #${reservation.stockUnitId}`)
      .filter(Boolean);
    return codes.length ? codes.join(', ') : '-';
  }

  scanSummary(line: WarehousingDispatch['lines'][number]): string {
    const reservations = line.reservations || [];
    if (!reservations.length) return '-';
    const picked = reservations.filter(row => this.scanRank(row.scanStatus) >= 1).length;
    const staged = reservations.filter(row => this.scanRank(row.scanStatus) >= 2).length;
    const loaded = reservations.filter(row => this.scanRank(row.scanStatus) >= 3).length;
    return `${picked}/${reservations.length} picked | ${staged}/${reservations.length} staged | ${loaded}/${reservations.length} loaded`;
  }

  reservationAuditRows(): Array<{ itemId: number; reservation: any }> {
    return (this.dispatch?.lines || []).flatMap(line => (line.reservations || []).map(reservation => ({
      itemId: line.itemId,
      reservation
    })));
  }

  hasInheritedReservationHint(): boolean {
    return this.reservationAuditRows().some(row => this.isInheritedReservation(row.reservation));
  }

  reservationBindingLabel(reservation: any): string {
    return (reservation?.reservationBindingMode
      || (this.isInheritedReservation(reservation) ? 'UPSTREAM_LOCKED' : 'DISPATCH_LOCKED')
      || '-')
      .replace(/_/g, ' ');
  }

  reservationSourceLabel(reservation: any): string {
    if (!this.isInheritedReservation(reservation)) {
      return 'Dispatch reserve';
    }
    const sourceType = reservation.sourceReservationType || 'Inherited reservation';
    const sourceId = reservation.sourceReservationId ? ` #${reservation.sourceReservationId}` : '';
    return `${sourceType}${sourceId}`;
  }

  reservationLocationLabel(reservation: any): string {
    const warehouse = this.masterName('WAREHOUSE', reservation?.stockUnitWarehouseId);
    const zone = this.masterName('ZONE', reservation?.stockUnitZoneId);
    const condition = this.masterName('ITEM_CONDITION', reservation?.stockUnitConditionId);
    return [warehouse, zone, condition].filter(value => value && value !== '-').join(' / ') || '-';
  }

  async printReservedUnitLabels(): Promise<void> {
    const stockUnitIds = (this.dispatch?.lines || [])
      .flatMap(line => line.reservations || [])
      .map(row => row.stockUnitId)
      .filter((value, index, values) => values.indexOf(value) === index);
    if (!stockUnitIds.length) {
      this.messageService.add({ severity: 'warn', summary: 'No Reserved Units', detail: 'Reserve stock before printing unit labels.' });
      return;
    }
    this.isPrintingLabels = true;
    this.warehousingService.printStockUnitLabels({ stockUnitIds, template: 'WAREHOUSE_STOCK_4X6' }).subscribe({
      next: async blob => {
        const mode = await this.labelPrintService.printOrDownload(blob, `${this.dispatch?.dispatchNumber || 'dispatch'}-unit-labels.zpl`);
        this.messageService.add({
          severity: 'success',
          summary: mode === 'printed' ? 'Labels Printed' : 'Labels Downloaded',
          detail: mode === 'printed' ? 'Labels were sent to the default Zebra printer.' : 'ZPL labels were downloaded.'
        });
        this.isPrintingLabels = false;
      },
      error: () => this.isPrintingLabels = false
    });
  }

  displayDateTime(value?: string): string {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  }

  private scanRank(scanStatus?: string): number {
    switch ((scanStatus || 'PENDING').toUpperCase()) {
      case 'PICKED': return 1;
      case 'STAGED': return 2;
      case 'LOADED': return 3;
      default: return 0;
    }
  }

  private reserveSuccessDetail(dispatch: WarehousingDispatch): string {
    const inherited = (dispatch.lines || []).some(line => (line.reservations || []).some(reservation => this.isInheritedReservation(reservation)));
    return inherited ? 'Dispatch bound reserved units from upstream warehouse reservations.' : 'Stock has been reserved.';
  }

  private isInheritedReservation(reservation: any): boolean {
    return !!(reservation?.sourceReservationType || reservation?.sourceReservationId);
  }

  statusSeverity = statusSeverity;
}
